import { useEffect, useState } from "react"
import { useForm, Controller } from "react-hook-form"
import styles from "./invite-team-member-page.module.scss"
import CustomerPageLayout from "../../../customer-page-layout/CustomerPageLayout"
import { useLazyGetRolesQuery } from "../../../../../service/role-service"
import { CustomerLoader } from "../../../components/customer-loader"
import { useNewTeamMemberInvitationMutation } from "../../../../../service/team-management-service"
import { useNavigate } from "react-router"
import { toast } from "react-toastify"
import { useTranslation } from "react-i18next"
import { CheckIcon } from "../../../../../assets/icons"
import { ApplicationRoutes } from "../../../../../routes"
import { roleDescriptions } from "../team-management.constant"
import { RootState } from "../../../../../stores/store"
import { useSelector } from "react-redux"
import { CustomerType } from "../../../../../model/customer/customer-type"
import { Select, SelectOption } from "../../../components/select"
import { Input } from "../../../components/input"

interface FormValues {
  email: string
  firstName: string
  lastName: string
  roleId: string
}

export const InviteTeamMemberPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userInfo = useSelector((state: RootState) => state.user);

  const [getTeamMemberRoles, { isLoading: isLoadingRoles, isFetching: isFetchingRoles }] = useLazyGetRolesQuery();
  const [newTeamMemberInvitation, { isLoading: isLoadingInvitation }] = useNewTeamMemberInvitationMutation();

  const [roleOptions, setRoleOptions] = useState<SelectOption[]>([])
  const [isInvitationSent, setIsInvitationSent] = useState(false)

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors }
  } = useForm<FormValues>({
    defaultValues: {
      email: "",
      firstName: "",
      lastName: "",
      roleId: ""
    }
  })

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const result = await getTeamMemberRoles({ Page: 1, PageSize: 100 }).unwrap();
        if (result) {
          setRoleOptions(result.items.map((x) => ({
            value: x.id,
            label: x.name,
            description: roleDescriptions[x.name] || t("common.labels.unknown")
          })));
        }
      } catch (error: any) {
        toast.error(error?.data?.title || t("account.web.failedToFetchRoles"));
      }
    };

    fetchRoles();
  }, [getTeamMemberRoles]);

  const onSubmit = async (data: FormValues) => {
    try {
      await newTeamMemberInvitation({
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        roleId: data.roleId
      }).unwrap();

      setIsInvitationSent(true);
      reset();
    } catch (error: any) {
      toast.error(error?.data?.title || t("account.web.failedToSendInvitation"));
    }
  }

  const handleCancel = () => {
    reset();
    navigate(-1);
  }

  if (isLoadingRoles || isFetchingRoles) {
    return (
      <CustomerPageLayout title={userInfo.customerType === CustomerType.Individual ? t("common.labels.familyAccountManagement") : t("account.corporateAccountManagement")}>
        <CustomerLoader
          title={userInfo.customerType === CustomerType.Individual ? t("account.web.loadingFamilyAccountManagement") : t("account.web.loadingCorporateAccountManagement")}
          subtitle={t("account.web.pleaseWaitFetchingInformation")}
        />
      </CustomerPageLayout>
    );
  }

  if (isLoadingInvitation) {
    return (
      <CustomerPageLayout title={userInfo.customerType === CustomerType.Individual ? t("common.labels.familyAccountManagement") : t("account.corporateAccountManagement")}>
        <CustomerLoader
          title={t("account.web.sendingInvitation")}
          subtitle={t("account.web.pleaseWaitProcessingRequest")}
        />
      </CustomerPageLayout>
    );
  }

  if (isInvitationSent) {
    return (
      <CustomerPageLayout title={userInfo.customerType === CustomerType.Individual ? t("common.labels.familyAccountManagement") : t("account.corporateAccountManagement")}>
        <div className={styles.container}>
          <div className={styles.successCard}>
            <div className={styles.successIconWrapper}>
              <CheckIcon />
            </div>
            <h1 className={styles.successTitle}>{t("corporate.invitationSuccessfull")}</h1>
            <p className={styles.successSubtitle}>{t("account.web.proceedToHomepageOrAddMember")}</p>
          </div>

          <div className={styles.successButtonGroup}>
            <button className={styles.proceedHomeButton} onClick={() => navigate(`/${ApplicationRoutes.DASHBOARD}`)}>
              {t("account.web.proceedToHomepage")}
            </button>
            <button className={styles.addNewMemberButton} onClick={() => navigate(-1)}>
              {t("account.web.addNewMember")}
            </button>
          </div>
        </div>
      </CustomerPageLayout>
    );
  }

  return (
    <CustomerPageLayout title={userInfo.customerType === CustomerType.Individual ? t("common.labels.familyAccountManagement") : t("account.corporateAccountManagement")}>
      <div className={styles.container}>
        <div className={styles.content}>
          <h1 className={styles.title}>{t("account.web.inviteNewMember")}</h1>
          <p className={styles.subtitle}>
            {userInfo.customerType === CustomerType.Individual ? t("common.labels.sendInvitationFamilyDescription") : t("account.web.sendInvitationCorporateAccount")}
          </p>

          <form className={styles.formContainer} onSubmit={handleSubmit(onSubmit)}>
            <Controller
              name="email"
              control={control}
              rules={{
                required: t("account.web.emailIsRequired"),
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: t("account.web.invalidEmailAddress")
                }
              }}
              render={({ field }) => (
                <Input
                  type="email"
                  placeholder={t("account.email")}
                  value={field.value}
                  onChange={field.onChange}
                  variant="solid"
                  error={errors.email?.message}
                />
              )}
            />

            <Controller
              name="firstName"
              control={control}
              rules={{
                required: t("common.validation.firstNameRequired"),
                minLength: {
                  value: 2,
                  message: t("account.web.firstNameMinLength2")
                }
              }}
              render={({ field }) => (
                <Input
                  type="text"
                  placeholder={t("common.labels.firstName")}
                  value={field.value}
                  onChange={field.onChange}
                  variant="solid"
                  error={errors.firstName?.message}
                />
              )}
            />

            <Controller
              name="lastName"
              control={control}
              rules={{
                required: t("common.validation.lastNameRequired"),
                minLength: {
                  value: 2,
                  message: t("account.web.lastNameMinLength2")
                }
              }}
              render={({ field }) => (
                <Input
                  type="text"
                  placeholder={t("common.labels.lastName")}
                  value={field.value}
                  onChange={field.onChange}
                  variant="solid"
                  error={errors.lastName?.message}
                />
              )}
            />

            <Controller
              name="roleId"
              control={control}
              rules={{ required: t("account.web.pleaseSelectARole") }}
              render={({ field }) => (
                <Select
                  options={roleOptions}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={t("corporate.assingRole")}
                  emptyMessage={t("account.web.noRolesAvailable")}
                  error={errors.roleId?.message}
                />
              )}
            />

            <div className={styles.buttonGroup}>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={handleCancel}
              >
                {t("common.actions.cancel")}
              </button>
              <button
                type="submit"
                className={styles.sendButton}
              >
                {t("corporate.sendInvitation")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </CustomerPageLayout>
  )
}
